import { useGlobal } from 'contexts/global-context'
import { Form, Formik } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'shared/catalyst-components/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from 'shared/catalyst-components/dialog'
import { Divider } from 'shared/catalyst-components/divider'
import Client from 'shared/clients/base/Client'
import DefaultClientOptions from 'shared/clients/DefaultClientOptions'
import { ReimbursementStatusBadge } from 'shared/components'
import ReimbursementStatusEnum from 'shared/models/enum/ReimbursementStatusEnum'
import TenantModulesEnum from 'shared/models/enum/TenantModulesEnum'
import FileAsset from 'shared/models/FileAsset'
import Location from 'shared/models/Location'
import LocationSearchRequest from 'shared/models/LocationSearchRequest'
import Reimbursement from 'shared/models/Reimbursement'
import convertToFileAsset from 'shared/utilities/convertToFileAsset'
import { displayAmount, displayDate } from 'shared/utilities/helpers'
import {
	FormikMultiFileUpload,
	FormikRadioGroup,
	FormikText,
	ReadOnlyField,
	Select,
	Spinner,
} from 'ui-toolkit-tailwind/src/components'
import { Col, Grid } from 'ui-toolkit-tailwind/src/components/FormLayouts'
import { showToast } from 'utilities/showToast'
import * as Yup from 'yup'
import { useCore } from '../../../contexts/core-context'

const TypeEnum = {
	VIEW: 'View', // [Owner's Portal, Revenue Module]
	CREATE: 'Create', // [Owner's Portal, Revenue Module]
	EDIT: 'Edit', // [Owner's Portal]
	EDIT_AND_REVIEW: 'EditReview', // [Revenue Module, Admin Only]
	REVIEW: 'Review', // [Revenue Module, Staff]
}

/** @type {Reimbursement} */
const defaultValues = {
	// for Owner's Portal
	amount: '',
	type: 'Reimbursement',
	description: '',
	receiptFileAssets: [],
	// for Revenue Module
	approvalFlag: null,
	evaluationReason: '',
}

/**
 * @typedef ReimbursementFormModalProps
 * @property {Location} location required when creating a new reimbursement
 * @property {Reimbursement} [reimbursement]
 * @property {boolean} visible
 * @property {boolean} isRevenueAdmin
 * @property {boolean} [isRevenueStaff] staff can edit reimbursements in Requested status
 * @property {() => void} onClose
 * @property {(reimbursement: Reimbursement) => void} onFinish
 */

/**
 * @param {ReimbursementFormModalProps} props
 */
const ReimbursementFormModal = ({
	location: originalLocation,
	reimbursement: originalReimbursement,
	visible,
	isRevenueAdmin,
	isRevenueStaff,
	onClose,
	onFinish,
}) => {
	const isOwnerPortal = process.env.REACT_APP_MODULE === TenantModulesEnum.OWNERS_PORTAL
	const isRevenueModule = process.env.REACT_APP_MODULE === TenantModulesEnum.REVENUE

	const { setShowSpinner, showConfirmation } = useGlobal()
	const [location, setLocation] = useState(/** @type {Location} */ (originalLocation))
	const [reimbursement, setReimbursement] = useState(/** @type {Reimbursement} */ (null))

	// get the type of the current form modal based on the what module we are in
	// and the current state of the reimbursement
	const type = useMemo(() => {
		if (!reimbursement || !reimbursement.id) return TypeEnum.CREATE

		if (isOwnerPortal) {
			return reimbursement.approvalFlag ? TypeEnum.VIEW : TypeEnum.EDIT
		}

		if (isRevenueModule) {
			// If status is approved/declined/paid/invoiced, only view is allowed
			if (
				[
					ReimbursementStatusEnum.APPROVED,
					ReimbursementStatusEnum.DECLINED,
					ReimbursementStatusEnum.PAID,
					ReimbursementStatusEnum.INVOICED,
				].includes(reimbursement.status)
			) {
				return TypeEnum.VIEW
			}
			// Staff can edit reimbursements in Requested status (edit only, no approval/decline)
			if (isRevenueStaff && reimbursement.status === ReimbursementStatusEnum.REQUESTED) {
				return TypeEnum.EDIT
			}
			// Admin can edit and review
			if (isRevenueAdmin) {
				return TypeEnum.EDIT_AND_REVIEW
			}
			// Otherwise, only review
			return TypeEnum.REVIEW
		}

		return null
	}, [isOwnerPortal, isRevenueModule, reimbursement, isRevenueAdmin, isRevenueStaff])

	useEffect(() => {
		if (visible) {
			setLocation(originalLocation)
		}
	}, [visible, originalLocation])

	const editRequestValidationSchema = Yup.object({
		amount: Yup.number()
			.typeError('Amount has to be a number')
			.moreThan(0, 'Amount has to be greater than 0')
			.required(),
		description: Yup.string().required(),
		receiptFileAssets: Yup.array().min(1, 'At least one attachment is required'),
	})
	const evaluateRequestValidationSchema = Yup.object({
		approvalFlag:
			type === TypeEnum.CREATE ? Yup.boolean().nullable() : Yup.boolean().nullable().required(),
		evaluationReason: Yup.string()
			.nullable()
			.when('approvalFlag', {
				is: false,
				then: schema => schema.required(),
			}),
	})

	const validationSchema = [TypeEnum.EDIT, TypeEnum.CREATE].includes(type)
		? editRequestValidationSchema
		: type === TypeEnum.EDIT_AND_REVIEW
		? editRequestValidationSchema.concat(evaluateRequestValidationSchema)
		: evaluateRequestValidationSchema

	const getActionButtonText = () => {
		if (type === TypeEnum.CREATE) return 'Submit'
		if (type === TypeEnum.EDIT) {
			if (reimbursement?.status === ReimbursementStatusEnum.DECLINED) return 'Re-Submit'
			else return 'Save'
		}
		if (type === TypeEnum.REVIEW) return 'Submit'
		if (type === TypeEnum.EDIT_AND_REVIEW) return 'Submit'
		return ''
	}

	useEffect(() => {
		if (visible) {
			if (originalReimbursement) {
				Client.RevenueApi.getReimbursement(String(originalReimbursement.id), {
					status200: setReimbursement,
				})
			} else {
				setReimbursement(defaultValues)
			}
		} else {
			setReimbursement(null)
		}
	}, [visible, originalReimbursement])

	/** @param {Reimbursement} values */
	const handleEditRequestSubmit = async values => {
		const request = Reimbursement.create(values)

		// convert the file assets to FileAsset objects
		const fileAssets = values.receiptFileAssets.map(async f => {
			const isExistingFileAsset = f instanceof FileAsset
			if (isExistingFileAsset) return f
			else return await convertToFileAsset(f)
		})
		request.receiptFileAssets = await Promise.all(fileAssets)

		if (type === TypeEnum.CREATE) {
			request.location = Location.create({ id: location.id })
		}

		Client.RevenueApi.saveReimbursement(
			request,
			{
				status200: response => {
					if (type === TypeEnum.EDIT_AND_REVIEW) {
						handleRevenueModuleSubmit(values)
						return
					}
					showToast({
						intent: 'success',
						title: `${response.type} Request`,
						content: `The ${response.type} request has been ${
							type === TypeEnum.CREATE ? 'created' : 'updated'
						}	successfully.`,
					})
					onClose()
					onFinish(response)
				},
			},
			{
				onApiCall: () => setShowSpinner(true),
				onApiResponse: () => setShowSpinner(false),
			},
		)
	}
	/** @param {Reimbursement} values */
	const handleRevenueModuleSubmit = async values => {
		const request = new Reimbursement()
		request.id = originalReimbursement.id
		request.approvalFlag = values.approvalFlag
		if (values.approvalFlag === false) request.evaluationReason = values.evaluationReason

		Client.RevenueApi.evaluateReimbursement(
			request,
			{
				status200: response => {
					showToast({
						intent: 'success',
						title: `${response.type} Request`,
						content: `Review has been submitted.`,
					})
					onClose()
					onFinish(response)
				},
			},
			{
				onApiCall: () => setShowSpinner(true),
				onApiResponse: () => setShowSpinner(false),
			},
		)
	}

	/** @param {Reimbursement} values */
	const handleFormSubmit = values => {
		if ([TypeEnum.EDIT, TypeEnum.EDIT_AND_REVIEW, TypeEnum.CREATE].includes(type))
			handleEditRequestSubmit(values)
		if (type === TypeEnum.REVIEW) handleRevenueModuleSubmit(values)
	}

	const handleDelete = async () => {
		const confirm = await showConfirmation({
			title: `Delete ${reimbursement?.type} Request`,
			content: `Are you sure you want to delete this ${reimbursement?.type} Request?`,
		})
		if (confirm) {
			Client.RevenueApi.deleteReimbursement(
				String(reimbursement.id),
				{
					status200: () => {
						showToast({
							intent: 'success',
							title: `${reimbursement?.type} Request`,
							content: 'The request has been deleted successfully.',
						})
						onClose()
						onFinish()
					},
				},
				{
					onApiCall: () => setShowSpinner(true),
					onApiResponse: () => setShowSpinner(false),
				},
			)
		}
	}

	const handleUndoApproval = async () => {
		const confirm = await showConfirmation({
			title: 'Undo Approval',
			content: 'Are you sure you want to undo the approval of this request?',
		})

		const request = new Reimbursement()
		request.id = originalReimbursement.id
		request.approvalFlag = null

		if (confirm) {
			Client.RevenueApi.evaluateReimbursement(
				request,
				{
					status200: () => {
						showToast({
							intent: 'success',
							title: 'Undo Approval',
							content: 'The approval has been undone successfully.',
						})
						onClose()
						onFinish()
					},
				},
				{
					onApiCall: () => setShowSpinner(true),
					onApiResponse: () => setShowSpinner(false),
				},
			)
		}
	}

	if (!reimbursement && visible) return <Spinner visible />

	return (
		<Formik
			enableReinitialize
			initialValues={reimbursement}
			onSubmit={handleFormSubmit}
			validationSchema={validationSchema}
		>
			{({ values, errors, touched, setValues, setFieldValue, resetForm, handleSubmit }) => {
				return (
					<Dialog size="3xl" open={visible} onClose={onClose}>
						<Form>
							<DialogTitle>
								{type === TypeEnum.CREATE
									? 'New'
									: type === TypeEnum.EDIT_AND_REVIEW
									? 'Edit and Review'
									: type}{' '}
								{reimbursement?.type} Request
							</DialogTitle>
							<DialogBody>
								<div className="space-y-3 md:space-y-6">
									<ReimbursementInformation
										location={location}
										setLocation={setLocation}
										reimbursement={reimbursement}
										type={type}
									/>
									{values && (
										<>
											<Divider />
											<ReimbursementForm
												values={values}
												type={type}
												isRevenueModule={isRevenueModule}
												isRevenueAdmin={isRevenueAdmin}
												isRevenueStaff={isRevenueStaff}
											/>
										</>
									)}
								</div>
							</DialogBody>
							<DialogActions>
								{type === TypeEnum.EDIT &&
									reimbursement?.status === ReimbursementStatusEnum.REQUESTED && (
										<Button plain className="mr-auto !text-danger-700" onClick={handleDelete}>
											Delete Request
										</Button>
									)}

								{isRevenueAdmin && reimbursement?.status === ReimbursementStatusEnum.DECLINED && (
									<Button plain className="mr-auto !text-danger-700" onClick={handleDelete}>
										Delete Request
									</Button>
								)}

							{isRevenueModule &&
								isRevenueAdmin &&
								reimbursement?.status === ReimbursementStatusEnum.APPROVED && (
									<Button plain className="mr-auto !text-danger-700" onClick={handleUndoApproval}>
										Undo Approval
									</Button>
								)}

								<Button plain outline={type === TypeEnum.VIEW} onClick={onClose}>
									{type === TypeEnum.VIEW ? 'Close' : 'Cancel'}
								</Button>
								{type !== TypeEnum.VIEW && <Button type="submit">{getActionButtonText()}</Button>}
							</DialogActions>
						</Form>
					</Dialog>
				)
			}}
		</Formik>
	)
}

/**
 * @typedef ReimbursementInformationProps
 * @property {Location} location
 * @property {Reimbursement} [reimbursement]
 * @property {string} type
 * @property {(location: Location) => void} setLocation
 */

/**
 * @param {ReimbursementInformationProps} props
 */
const ReimbursementInformation = ({ location, reimbursement, type, setLocation }) => {
	const { tenantMembership } = useCore()

	const [locations, setLocations] = useState(/** @type {Location[]} */ ([]))
	const locationOptions = locations?.map(location => ({
		label: location.name,
		value: location.id,
	}))

	const queryLocations = useCallback(() => {
		if (!tenantMembership?.tenant?.id) return

		const request = new LocationSearchRequest()
		request.tenantId = tenantMembership.tenant.id
		Client.LocationApi.search(request, {
			status200: response => setLocations(response.locations),
		})
	}, [tenantMembership])

	useEffect(() => {
		if (!location) queryLocations()
	}, [location, queryLocations])

	return (
		<Grid tighten>
			<Col span={3}>
				{reimbursement?.location?.name || location?.name ? (
					<ReadOnlyField label="Location" value={reimbursement?.location?.name || location?.name} />
				) : (
					type === TypeEnum.CREATE && (
						<Select
							value={location?.id}
							onChange={v => setLocation(locations.find(l => l.id === v))}
							label="Select Location"
							options={locationOptions}
						/>
					)
				)}
			</Col>

			{type !== TypeEnum.CREATE && reimbursement && (
				<>
					<Col span={3}>
						<ReadOnlyField
							label="Status"
							value={<ReimbursementStatusBadge status={reimbursement.status} />}
						/>
					</Col>

					<Col span={3}>
						<ReadOnlyField
							label="Date Requested"
							value={`${displayDate(reimbursement.dateRequested)} by ${
								reimbursement.requestedByPerson?.name
							}`}
						/>
					</Col>

					{reimbursement.status !== ReimbursementStatusEnum.REQUESTED && (
						<>
							<Col span={3}>
								<ReadOnlyField
									label={
										reimbursement?.status === ReimbursementStatusEnum.DECLINED
											? 'Date Declined'
											: 'Date Approved'
									}
									value={`${displayDate(reimbursement.dateEvaluated)} by ${
										reimbursement.evaluatedByPerson?.name
									}`}
								/>
							</Col>

							{reimbursement.status === ReimbursementStatusEnum.DECLINED && (
								<Col span={6}>
									<ReadOnlyField label="Reason" value={reimbursement.evaluationReason || '-'} />
								</Col>
							)}

							{[
								ReimbursementStatusEnum.APPROVED,
								ReimbursementStatusEnum.PAID,
								ReimbursementStatusEnum.INVOICED,
							].includes(reimbursement.status) && (
								<>
									<Col span={3}>
										<ReadOnlyField
											label="Date Invoiced"
											value={displayDate(reimbursement.dateInvoiced) || '-'}
										/>
									</Col>
									<Col span={3}>
										<ReadOnlyField
											label="Date Paid"
											value={displayDate(reimbursement.datePaid) || '-'}
										/>
									</Col>
								</>
							)}
						</>
					)}
				</>
			)}
		</Grid>
	)
}

/**
 * @typedef ReimbursementFormProps
 * @property {string} type
 * @property {Reimbursement} values
 * @property {boolean} isRevenueModule
 * @property {boolean} [isRevenueAdmin]
 * @property {boolean} [isRevenueStaff]
 */

/**
 * @param {ReimbursementFormProps} props
 */
const ReimbursementForm = ({ values, type, isRevenueModule, isRevenueAdmin, isRevenueStaff }) => {
	if ([TypeEnum.EDIT, TypeEnum.EDIT_AND_REVIEW].includes(type)) {
		values.amount = Math.abs(values.amount)
	}

	// Show Response (Approve/Decline) only for Admin when editing and reviewing
	// Staff can only edit, not approve/decline
	const showResponse = (type === TypeEnum.EDIT_AND_REVIEW && isRevenueAdmin) || type === TypeEnum.REVIEW

	return (
		<>
			{[TypeEnum.CREATE, TypeEnum.EDIT, TypeEnum.EDIT_AND_REVIEW].includes(type) ? (
				<Grid tighten>
					{isRevenueModule && (
						<Col span={6}>
							<FormikRadioGroup
								inline
								name="type"
								label="Type"
								options={[
									{ label: 'Reimbursement', value: 'Reimbursement' },
									{ label: 'Expense', value: 'Expense' },
								]}
							/>
						</Col>
					)}
					<Col span={2}>
						<FormikText leadingAddon="$" name="amount" label="Amount" />
					</Col>
					<Col span={4}>
						<FormikText name="description" label="Description" />
					</Col>
					<Col span={6}>
						<FormikMultiFileUpload
							endpointUrl={DefaultClientOptions.getEndpointUrl()}
							name="receiptFileAssets"
							label="Attachment(s)"
							helperText="Supported file types: .jpg, .jpeg, .png, .pdf"
							accept=".jpg,.jpeg,.png,.pdf"
						/>
					</Col>
				</Grid>
			) : (
				<Grid tighten>
					<Col span={3}>
						<ReadOnlyField label="Amount" value={displayAmount(values.amount)} />
					</Col>
					<Col span={3}>
						<ReadOnlyField label="Description" value={values.description || '-'} />
					</Col>
					<Col span={6}>
						<ReadOnlyField
							label="Attachment(s)"
							value={
								<ul>
									{values.receiptFileAssets?.map(file => (
										<li key={file.id}>
											<a
												className="link"
												href={`${DefaultClientOptions.getEndpointUrl()}/${file.viewUrl}`}
												target="_blank"
												rel="noreferrer"
											>
												{file.filename}
											</a>
										</li>
									))}
								</ul>
							}
						/>
					</Col>
				</Grid>
			)}

			{showResponse && (
				<Grid tighten>
					<Col span={3}>
						<FormikRadioGroup
							inline
							name="approvalFlag"
							label="Response"
							options={[
								{ label: 'Approve', value: true },
								{ label: 'Decline', value: false },
							]}
						/>
					</Col>
					{values.approvalFlag === false && (
						<Col span={6}>
							<FormikText name="evaluationReason" label="Reason" />
						</Col>
					)}
				</Grid>
			)}
		</>
	)
}

export default ReimbursementFormModal
